import { WidgetFeature } from 'enums/WidgetFeature';
import {
    HandleRouting,
    NavigateChangeParams,
    NavigatePushState,
    NavigateUpdateParams,
} from 'interfaces/Navigate';
import { DefaultRootState } from 'react-redux';
import { services } from 'services/container';
import { setChatOpen } from 'state/chat/chat.actions';

import { widgetStore } from './widgetStore';

export class Navigate {
    private static getPathname(pathname?: string) {
        return pathname || location.pathname;
    }

    private static getUrl(pathname?: string) {
        const url = new URL(String(location));
        url.pathname = this.getPathname(pathname);

        return url;
    }

    public static getIsRoutingEnabled() {
        return (
            !!(!!widgetStore.store || process.env.NODE_ENV === 'test') &&
            (() => {
                const store = widgetStore.store.getState() as DefaultRootState;
                const defaultValue = store.features[WidgetFeature.UseHistoryAPI];
                const overrideValue =
                    store.parameters?.overrideFeatures?.[WidgetFeature.UseHistoryAPI];

                return Boolean(typeof overrideValue === 'undefined' ? defaultValue : overrideValue);
            })()
        );
    }

    private static handleRouting({ url, replace, state = {} }: HandleRouting) {
        (!!widgetStore.store || process.env.NODE_ENV === 'test') &&
            (() => {
                const store = widgetStore.store.getState() as DefaultRootState;

                const isRoutingEnabled = this.getIsRoutingEnabled();

                if (isRoutingEnabled) {
                    replace
                        ? history.replaceState({ ...history.state, ...state }, '', url)
                        : history.pushState({ ...history.state, ...state }, '', url);
                } else {
                    const newOpenState = !store.chat.open;
                    widgetStore.store.dispatch(setChatOpen(newOpenState));
                }

                services.widget.eventChannel.emit('forceUpdateSearchParamsDependency', false);
            })();
    }

    public static navigate({ pathname, params = {}, replace = false, state }: NavigatePushState) {
        const url = this.getUrl(pathname);

        Object.entries(params).forEach(([param, value]) => {
            url.searchParams.set(param, `"${value}"`);
        });

        this.handleRouting({
            url,
            state,
            replace,
        });
    }

    public static changeQueryParams({
        params = {},
        replace = false,
        state,
        isAppend = false,
    }: NavigateChangeParams) {
        const url = this.getUrl();

        Object.entries(params).forEach(([param, value]) => {
            if (isAppend) {
                url.searchParams.set(param, `"${value}"`);
            } else {
                url.searchParams.delete(param);
            }
        });

        this.handleRouting({
            url,
            state,
            replace,
        });
    }

    public static updateQueryParams({
        params,
        replace,
        state,
        isAppend = false,
    }: NavigateUpdateParams) {
        const url = this.getUrl();

        (Array.isArray(params) ? params : Object.entries(params)).forEach((argv) => {
            const args = isAppend ? [argv[0], argv[1]] : [argv];
            url.searchParams[isAppend ? 'set' : 'delete'](...(args as [string, string?]));
        });

        this.handleRouting({
            state,
            replace,
            url,
        });
    }

    public static goBack() {
        const isRoutingEnabled = this.getIsRoutingEnabled();

        if (isRoutingEnabled) {
            history.back();
        } else {
            const store = widgetStore.store.getState() as DefaultRootState;
            const newOpenState = !store.chat.open;
            widgetStore.store.dispatch(setChatOpen(newOpenState));
        }
    }
}
